import ClientModule from "@/store/modules/client.module";
import { toaster } from "@/utilities/toaster";
import { assign, Machine, send } from "xstate";
const teamOperationsMachine = Machine({
    id: "teamOperations",
    type: "parallel",
    context: {
        teamId: "",
        selectedSection: "communication",
        loadingSection: "communication",
        editSection: null,
        historyField: null,
    },
    states: {
        // One tab at a time: the first tab on open, a tab each time it is selected, the saved tab after a save
        dataInfo: {
            initial: "loading",
            on: {
                // External: restarts the load even while another tab is loading
                LOAD: { target: ".loading", internal: false, actions: "assignLoadingSection" },
                SELECT_SECTION: { target: ".loading", internal: false, actions: ["assignSelectedSection", "assignLoadingSection"] },
            },
            states: {
                loading: {
                    invoke: {
                        id: "loadingTeamOperations",
                        src: (context, event) => {
                            return ClientModule.GetTeamOperations({ teamId: context.teamId, section: context.loadingSection });
                        },
                        onDone: { target: "loaded" },
                        onError: { target: "failed" },
                    },
                },
                loaded: {},
                failed: {},
            },
        },
        // A section's Add / Edit modal and its save
        editInfo: {
            initial: "idle",
            states: {
                idle: {
                    on: {
                        EDIT: { target: "editing", actions: "assignEditSection", cond: "canEdit" },
                    },
                },
                editing: {
                    on: {
                        SAVE: { target: "saving" },
                        CANCEL_EDIT: { target: "idle" },
                    },
                },
                saving: {
                    invoke: {
                        id: "savingTeamOperations",
                        src: (context, event) => {
                            return ClientModule.SaveTeamOperations({ section: context.editSection, dto: event.payLoad });
                        },
                        onDone: { target: "idle", actions: ["showSuccessMessage", "reloadEditSection"] },
                        // Back to the open modal with the edits kept; api.service has already shown the error toast
                        onError: { target: "editing" },
                    },
                },
            },
        },
        // Field history modal
        historyInfo: {
            initial: "idle",
            states: {
                idle: {
                    on: {
                        OPEN_HISTORY: { target: "loadingHistory", actions: "assignHistoryField" },
                    },
                },
                loadingHistory: {
                    invoke: {
                        id: "loadingTeamOperationHistory",
                        src: (context, event) => {
                            const field = context.historyField;
                            const attributeIds = field.attributeIds && field.attributeIds.length ? field.attributeIds : [field.attributeId];
                            return ClientModule.GetTeamOperationFieldHistory({ teamId: context.teamId, itemId: field.itemId, attributeIds });
                        },
                        onDone: { target: "showingHistory" },
                        onError: { target: "idle", actions: ["clearHistoryField", "resetHistoryStore"] },
                    },
                    on: {
                        CLOSE_HISTORY: { target: "idle", actions: ["clearHistoryField", "resetHistoryStore"] },
                    },
                },
                showingHistory: {
                    on: {
                        CLOSE_HISTORY: { target: "idle", actions: ["clearHistoryField", "resetHistoryStore"] },
                    },
                },
            },
        },
    },
}, {
    actions: {
        showSuccessMessage: (context, event) => {
            if (event.data && event.data.successMessages && event.data.successMessages.length) {
                toaster.ShowSuccess(event.data.successMessages[0]);
            }
        },
        assignSelectedSection: assign({
            selectedSection: (context, event) => event.section,
        }),
        assignLoadingSection: assign({
            loadingSection: (context, event) => event.section,
        }),
        assignEditSection: assign({
            editSection: (context, event) => event.section,
        }),
        assignHistoryField: assign({
            historyField: (context, event) => event.field,
        }),
        clearHistoryField: assign({
            historyField: (context, event) => null,
        }),
        resetHistoryStore: (context, event) => {
            ClientModule.SetTeamOperationHistory(null);
        },
        // Only the saved tab is fetched again
        reloadEditSection: send((context) => ({ type: "LOAD", section: context.editSection })),
    },
    guards: {
        // Operations of a deactivated team or client are read-only
        canEdit: (context, event) => !!ClientModule.teamOperations && ClientModule.teamOperations.isTeamActive,
    },
    activities: {},
    services: {},
});
export default teamOperationsMachine;
